/**
 * Per-field help copy for the area Auto-Placement editor
 * (components/builder/forms/AutoPlacementSection.tsx).
 *
 * Keyed by the `ap_*` react-hook-form field name. Rendered by <FieldHelp> in a
 * click-popover next to each label. Wording reflects the real domain semantics
 * in `area-placement.ts`, the parking-mode migrations, and docs/018-masters-demo/.
 */

import type { FieldHelpContent } from '@/components/ui/field-help'

export const AREA_PLACEMENT_HELP: Record<string, FieldHelpContent> = {
  ap_auto_placement: {
    title: 'Auto-placement',
    summary:
      'When on, the placement engine can pre-place inbound aircraft into this area using the rules below. When off, the area is authored but only filled manually.',
  },

  ap_area_category: {
    title: 'Placement category',
    summary:
      'The role this area plays when the engine assigns aircraft — independent of its structural type.',
    options: [
      { label: 'Parking', description: 'Long-term / overnight parking positions.' },
      {
        label: 'Tiedown',
        description: 'Tie-down spots, typically for based or longer-stay light aircraft.',
      },
      {
        label: 'Ramp',
        description: 'Active service ramp — fueling, quick-turns, vehicle access around aircraft.',
      },
    ],
  },

  ap_fill_priority: {
    title: 'Fill priority',
    summary:
      'Order this area fills relative to others — lower numbers fill first. Use it to prefer premium or convenient areas before overflow areas.',
  },

  ap_active_from: {
    title: 'Active from',
    summary:
      'Start of the window when this area exists on the map and accepts placement. Useful for event-only zones; outside the window it is inactive.',
  },
  ap_active_until: {
    title: 'Active until',
    summary:
      'End of the active window — placement stops after this time. Leave blank for an always-on area.',
  },
  ap_release_from: {
    title: 'Release from (optional)',
    summary:
      'Start of the expected departure push for aircraft parked here. Helps the engine sequence retrieval and free space for new arrivals.',
  },
  ap_release_until: {
    title: 'Release until (optional)',
    summary:
      'End of the release period. Together with Release from, it bounds when this area drains.',
  },

  ap_enforcement: {
    title: 'Enforcement',
    summary: 'Whether the eligibility rules below are binding or advisory.',
    options: [
      {
        label: 'Hard (robot never overrides)',
        description:
          'Rules are hard gates — aircraft that fail any rule are never placed here, even if every other area is full.',
      },
      {
        label: 'Soft (nudge / preference)',
        description:
          'Rules are preferences — the engine prefers matching aircraft but may place a non-match here on overflow.',
      },
    ],
  },

  ap_turnover_mode: {
    title: 'Stay profile',
    summary: 'The kind of traffic this area is meant for, by length of stay.',
    options: [
      {
        label: 'Drop-and-Go (quick-turn / fuel)',
        description: 'Short stops only; arrivals over the Max stay limit route elsewhere.',
      },
      {
        label: 'Parking (overnight)',
        description: 'Overnight / multi-day parking; the Max stay limit is ignored.',
      },
      { label: 'Mixed (both)', description: 'Accepts both quick-turns and overnight stays.' },
    ],
  },

  ap_dwell_max_minutes: {
    title: 'Max stay (minutes)',
    summary:
      'Longest planned stay this area accepts; arrivals expecting to stay longer are routed to a parking area.',
    note: 'Applies only to Drop-and-Go and Mixed stay profiles. e.g. 240 = up to a 4-hour stay.',
  },

  ap_parking_mode: {
    title: 'Parking mode',
    summary:
      'How aircraft reach their spot — under their own power or by tug. Drives tug labor and taxi routing.',
    options: [
      {
        label: 'Self-park only (taxi nose-in)',
        description: 'Aircraft taxi in under their own power; no tug needed.',
      },
      { label: 'Tow only (tug to spot)', description: 'Every aircraft is towed to its spot by a tug.' },
      {
        label: 'Both (tow large aircraft, else self-park)',
        description:
          'Hybrid — aircraft at or above the wingspan threshold are towed; smaller ones self-park.',
      },
    ],
  },

  ap_tow_min_wingspan_ft: {
    title: 'Tow threshold (wingspan, ft)',
    summary:
      'In Both mode, aircraft with a wingspan at or above this value are towed; narrower aircraft self-park.',
    note: "Only used when Parking mode is Both. Blank = the location's default threshold. e.g. 60 ft tows super-mids and larger.",
  },

  ap_max_wingspan_ft: {
    title: 'Max wingspan (ft)',
    summary:
      'Largest wingspan this area accepts; wider aircraft are filtered out (or de-prioritized if Enforcement is Soft).',
    note: 'With Uniform pitch, this also sets the fixed slot width. Blank = no cap.',
  },
  ap_max_length_ft: {
    title: 'Max length (ft)',
    summary:
      "Longest fuselage this area accepts; matters for shallow areas that can't fit large-cabin jets.",
    note: 'With Uniform pitch, this also sets the fixed slot length. Blank = no cap.',
  },

  ap_allowed_categories: {
    title: 'Eligible aircraft types',
    summary:
      'Restrict this area to specific aircraft categories — the simplest way to route traffic by type, e.g. send jets to one area and pistons / turboprops to their own.',
    note: 'Empty = all types welcome. The jet classes (very-light → ultra-long-range) let you separate different sizes of jets across areas. Combines with the size caps, client list, and Enforcement setting as part of the eligibility gate.',
  },

  ap_allowed_client_ids: {
    title: 'Allowed clients',
    summary:
      'Restrict this area to specific clients — e.g. a reserved VIP row. Only the listed clients can be placed here.',
    note: 'Empty = open to all clients.',
  },

  ap_pattern: {
    title: 'Layout pattern',
    summary: 'The geometric template the engine uses to arrange aircraft in this area.',
    options: [
      { label: 'Row', description: 'A single line of aircraft along the area.' },
      {
        label: 'Checkerboard',
        description: 'Staggered rows that interleave for denser packing.',
      },
      { label: 'Double Row', description: 'Two parallel lanes; their relationship is set by Coupling.' },
      {
        label: 'Free Stack',
        description: 'No fixed template — an optimizer packs aircraft freely to maximize fit.',
      },
    ],
  },

  ap_placement_heading: {
    title: 'Placement heading',
    summary:
      'Compass direction (0–359°) the aircraft noses point when auto-placed. Align it with how aircraft taxi in or face the apron edge.',
  },

  ap_progression: {
    title: 'Progression',
    summary: 'How successive aircraft step along a lane.',
    options: [
      {
        label: 'Side by Side (wingtip to wingtip)',
        description: 'Aircraft line up across the lane, wings adjacent — like cars in parking stalls.',
      },
      {
        label: 'Nose to Tail (single file)',
        description: 'Aircraft queue front-to-back in a single file along the lane.',
      },
    ],
  },

  ap_lanes: {
    title: 'Lanes',
    summary: 'Number of parallel rows of aircraft in this area.',
    options: [
      { label: '1 (Row)', description: 'A single row.' },
      {
        label: '2 (Double Row)',
        description: "Two parallel rows; the second row's orientation is set by Coupling.",
      },
    ],
  },

  ap_coupling: {
    title: 'Lane coupling',
    summary: 'How the second lane relates to the first in a Double Row.',
    options: [
      {
        label: 'Tail to Tail (noses out both sides)',
        description:
          'Lanes back onto a shared spine with noses outward; either side retrieves without blocking the other.',
      },
      {
        label: 'Nested (tails tuck into wing gaps)',
        description:
          "Second lane offset half a slot so tails tuck between the other lane's wings — denser, still no mutual blocking.",
      },
      {
        label: 'Same Direction (second depth row)',
        description:
          'Second row sits directly behind the first, same heading; the back row is blocked by the front, so it needs a front-to-back departure order.',
      },
    ],
  },

  ap_pitch_mode: {
    title: 'Pitch mode',
    summary: 'How wide each aircraft slot is sized.',
    options: [
      {
        label: 'Variable (each aircraft consumes its footprint)',
        description: 'Each slot is sized to that aircraft — tighter packing, but a departure may reflow neighbors.',
      },
      {
        label: 'Uniform (fixed slots sized to the largest type)',
        description:
          'Every slot is identical, sized to the Max caps — looser, but a departure leaves a clean reusable slot. Best for high-turnover ramps.',
      },
    ],
  },

  ap_safety_margin_ft: {
    title: 'Safety margin (ft)',
    summary:
      'Minimum edge-to-edge gap kept between adjacent aircraft (wingtip-to-wingtip or nose-to-tail). Larger margins are safer but fit fewer aircraft.',
    note: 'Blank = the location default. Typical: 10–20 ft.',
  },

  ap_auto_sort: {
    title: 'Auto-sort',
    summary: 'How aircraft are ordered into spots, so the right ones are easy to pull out.',
    options: [
      { label: 'Arrival order', description: 'Fill in the order aircraft arrive (first-come, first-served).' },
      {
        label: 'Departure order',
        description:
          "Place earliest-departing aircraft in the easiest-to-reach spots, so the morning push isn't blocked in.",
      },
      {
        label: 'Balanced',
        description: 'A compromise between arrival and departure ordering (good default).',
      },
    ],
  },

  ap_taxiway_exit_point_id: {
    title: 'Taxiway exit',
    summary:
      'Which runway/taxiway exit aircraft use to reach this area. Affects taxi routing and distance.',
    note: 'Leave on Nearest (auto) to let the engine pick the closest exit.',
  },
  ap_fallback_area_id: {
    title: 'Fallback area (overflow)',
    summary: 'Where aircraft go when this area is full — an explicit overflow target.',
    note: 'Default routes to the next area by fill priority.',
  },
}
